"use client";

import React, { useEffect, useRef } from "react";
import {
  CHANNEL_LOGO_IMAGE_ATTRIBUTE,
  applyChannelLogoFallback,
  hasFailedToLoad
} from "./channel-logo.shared";

/**
 * A client boundary earned by runtime measurement: whether a logo URL serves anything is only known
 * after it loads. One capture-phase listener on the lineup (`error` does not bubble) covers every
 * logo, including rows built later, so `ChannelLogo` stays a Server Component.
 */
export function LogoFallback() {
  const markerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const scope = markerRef.current?.parentElement;
    if (!scope) {
      return undefined;
    }
    const onError = (event: Event) => {
      applyChannelLogoFallback(event.target);
    };
    scope.addEventListener("error", onError, true);
    // Errors that fired before hydration left no event to hear.
    scope.querySelectorAll<HTMLImageElement>(`img[${CHANNEL_LOGO_IMAGE_ATTRIBUTE}]`).forEach(image => {
      if (hasFailedToLoad(image)) {
        applyChannelLogoFallback(image);
      }
    });
    return () => scope.removeEventListener("error", onError, true);
  }, []);

  return <span aria-hidden="true" hidden ref={markerRef} />;
}

export default LogoFallback;
